// src/services/authService.js
// Authentication service — backed by the AfriClimate Auth API (api-master)

import { AUTH } from './config';

const JSON_HEADERS = { 'Content-Type': 'application/json' };

const callAuth = async (path, options = {}) => {
  const res = await fetch(`${AUTH.BASE_URL}${path}`, {
    ...options,
    headers: { ...JSON_HEADERS, ...options.headers },
    signal: options.signal ?? AbortSignal.timeout(AUTH.TIMEOUT_MS),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.message || `HTTP ${res.status}`);
    // Attach extra fields the caller may need (e.g. userId when email unverified)
    if (data.userId) err.userId = data.userId;
    throw err;
  }
  return data;
};

class AuthService {
  constructor() {
    this.token = null;
    this.refreshToken = null;
    this.user = null;
    this._loadFromStorage();
  }

  _loadFromStorage() {
    try {
      this.token = localStorage.getItem('authToken');
      this.refreshToken = localStorage.getItem('refreshToken');
      const raw = localStorage.getItem('userData');
      if (raw) this.user = JSON.parse(raw);
    } catch {
      this._clearStorage();
    }
  }

  _saveToStorage() {
    if (this.token)        localStorage.setItem('authToken',    this.token);
    if (this.refreshToken) localStorage.setItem('refreshToken', this.refreshToken);
    if (this.user)         localStorage.setItem('userData',     JSON.stringify(this.user));
  }

  _clearStorage() {
    this.token = this.refreshToken = this.user = null;
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userData');
  }

  isAuthenticated() { return !!this.token; }
  getCurrentUser()  { return this.user; }

  getAuthHeaders() {
    const h = { ...JSON_HEADERS };
    if (this.token) h['Authorization'] = `Bearer ${this.token}`;
    return h;
  }

  // ── Register ───────────────────────────────────────────────────────────────

  async register(userData, verificationFile = null) {
    let fetchBody, fetchHeaders = {};

    if (verificationFile) {
      // Multipart FormData for file upload
      const fd = new FormData();
      fd.append('firstName',   userData.firstName);
      fd.append('lastName',    userData.lastName);
      fd.append('username',    userData.username);
      fd.append('email',       userData.email);
      fd.append('password',    userData.password);
      fd.append('accountType', userData.accountType || 'regular');
      if (userData.phone)            fd.append('phone',            userData.phone);
      if (userData.institution)      fd.append('institution',      userData.institution);
      if (userData.studentId)        fd.append('studentId',        userData.studentId);
      if (userData.course)           fd.append('course',           userData.course);
      if (userData.yearOfStudy)      fd.append('yearOfStudy',      userData.yearOfStudy);
      if (userData.organizationName) fd.append('organizationName', userData.organizationName);
      if (userData.organizationRole) fd.append('organizationRole', userData.organizationRole);
      fd.append('verificationDoc', verificationFile);
      fetchBody = fd;
      // Don't set Content-Type — browser auto-sets multipart boundary
    } else {
      fetchBody = JSON.stringify({
        firstName:        userData.firstName,
        lastName:         userData.lastName,
        username:         userData.username,
        email:            userData.email,
        password:         userData.password,
        accountType:      userData.accountType || 'regular',
        phone:            userData.phone            || undefined,
        institution:      userData.institution      || undefined,
        studentId:        userData.studentId        || undefined,
        course:           userData.course           || undefined,
        yearOfStudy:      userData.yearOfStudy      || undefined,
        organizationName: userData.organizationName || undefined,
        organizationRole: userData.organizationRole || undefined,
      });
      fetchHeaders = { 'Content-Type': 'application/json' };
    }

    const res = await fetch(`${AUTH.BASE_URL}/register`, {
      method: 'POST',
      body:   fetchBody,
      headers: fetchHeaders,
      signal: AbortSignal.timeout(AUTH.TIMEOUT_MS),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.message || `HTTP ${res.status}`);

    return {
      success:          true,
      userId:           data.userId,
      email:            data.email,
      accountType:      data.accountType,
      requiresApproval: data.requiresApproval || false,
      message:          data.message,
    };
  }

  // ── Verify email OTP ───────────────────────────────────────────────────────

  async verifyEmail(userId, otp) {
    const data = await callAuth('/verify-email', {
      method: 'POST',
      body: JSON.stringify({ userId, otp }),
    });

    this.token        = data.accessToken;
    this.refreshToken = data.refreshToken;
    this.user         = this._normalizeUser(data.user);
    this._saveToStorage();
    return { success: true, user: this.user };
  }

  // ── Resend OTP ─────────────────────────────────────────────────────────────

  async resendOtp(userId) {
    return callAuth('/resend-otp', { method: 'POST', body: JSON.stringify({ userId }) });
  }

  // ── Login ──────────────────────────────────────────────────────────────────

  async login(email, password) {
    const data = await callAuth('/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });

    this.token        = data.access_token;
    this.refreshToken = data.refresh_token;
    this.user         = this._normalizeUser(data.user);
    this._saveToStorage();

    return { success: true, token: this.token, user: this.user };
  }

  // ── Logout ─────────────────────────────────────────────────────────────────

  async logout() {
    try {
      if (this.token) {
        await callAuth('/logout', {
          method: 'POST',
          headers: { Authorization: `Bearer ${this.token}` },
          body: JSON.stringify({ refresh_token: this.refreshToken }),
        });
      }
    } catch { /* ignore */ }
    this._clearStorage();
    return { success: true };
  }

  // ── Refresh token ──────────────────────────────────────────────────────────

  async refreshAccessToken() {
    if (!this.refreshToken) throw new Error('No refresh token available');
    const data = await callAuth('/refresh', {
      method: 'POST',
      body: JSON.stringify({ refresh_token: this.refreshToken }),
    });
    this.token        = data.access_token;
    this.refreshToken = data.refresh_token;
    localStorage.setItem('authToken',    this.token);
    localStorage.setItem('refreshToken', this.refreshToken);
    return { success: true, token: this.token };
  }

  // ── Update own profile ─────────────────────────────────────────────────────

  async updateProfile(fields) {
    const data = await callAuth('/me', {
      method: 'PATCH',
      body: JSON.stringify(fields),
      headers: { Authorization: `Bearer ${this.token}` },
    });
    this.user = this._normalizeUser(data.user);
    this._saveToStorage();
    return { success: true, user: this.user };
  }

  // ── Change password ────────────────────────────────────────────────────────

  async changePassword(currentPassword, newPassword) {
    return callAuth('/me/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
      headers: { Authorization: `Bearer ${this.token}` },
    });
  }

  // ── Forgot / reset password ────────────────────────────────────────────────

  async requestPasswordReset(email) {
    return callAuth('/forgot-password', { method: 'POST', body: JSON.stringify({ email }) });
  }

  async resetPassword(email, otp, newPassword) {
    return callAuth('/reset-password', {
      method: 'POST',
      body: JSON.stringify({ email, otp, newPassword }),
    });
  }

  // ── Verify OTP (alias for verifyEmail, used by EnhancedAuthModal) ──────────

  async verifyOTP(userId, otp) {
    return this.verifyEmail(userId, otp);
  }

  // ── Helper ─────────────────────────────────────────────────────────────────

  _normalizeUser(u) {
    if (!u) return null;
    return {
      id:              u.id,
      username:        u.username,
      email:           u.email,
      firstName:       u.firstName,
      lastName:        u.lastName,
      fullName:        `${u.firstName || ''} ${u.lastName || ''}`.trim(),
      phone:           u.phone,
      role:            u.role || 'user',
      isStudent:       u.isStudent || false,
      isApproved:      u.isApproved || false,
      institution:     u.institution,
      organizationName: u.organizationName,
      assignedDevices: u.assignedDevices || [],
    };
  }
}

const authService = new AuthService();
export default authService;
